import './contactForm.scss';

import React, { useCallback, useContext, useEffect, useState } from 'react';
import { graphql } from 'gatsby';
import { useTranslation } from 'react-i18next';
import { usePageLocalization } from '@sbi-international/boutik.gatsby.plugin.multilocale';

import {
    Button,
    Input,
} from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Primitives';
import { CountryNameSelect } from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Account/CountryNameSelect';
import { ProvinceNameInput } from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Account/ProvinceNameInput';
import Layout from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Layout/Layout';
import SEO from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Seo';
import Select from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Primitives/Select';
import arrowIconWhite from '@progymedia/gatsby-theme-boutik-ecommerce/src/images/icons/Icon-Fleche.svg';
import { feedbackContext } from '@progymedia/gatsby-theme-boutik-ecommerce/src/context/feedback/feedbackContext';
import { validateEmail } from '@progymedia/gatsby-theme-boutik-ecommerce/src/utils/validations';
import DroletButton from '../../components/Primitives/DroletButton';

export const query = graphql`
    query {
        allProductsJson(
            filter: {
                category: { regex: "/Drolet/Heaters/" }
                status: { eq: "actif" }
            }
        ) {
            nodes {
                code
                category
                title {
                    fr_CA
                    en_CA
                    en_US
                }
            }
        }
        metadata: allProductsJson(
            filter: { code: { eq: "drolet_form-customer-service" } }
        ) {
            nodes {
                code
                attributes {
                    meta_title {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                    meta_description {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                }
            }
        }
    }
`;

function CustomerService({ data }) {
    const [product, setProduct] = useState('');
    const [date, setDate] = useState('YYYY-MM-DD');
    const [question, setQuestion] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [address, setAddress] = useState('');
    const [zipCode, setZipCode] = useState('');
    const [city, setCity] = useState('');
    const [countryName, setCountryName] = useState('');
    const [provinceName, setProvinceName] = useState('');
    const [isProvinceNameRequired, setIsProvinceNameRequired] = useState(false);

    const [isLoading, setIsLoading] = useState<boolean>(false);

    const { currentLocale, navigateTranslated } = usePageLocalization();
    const metadataAttributes = data?.metadata?.nodes[0]?.attributes;
    const metadata = {
        title: metadataAttributes?.meta_title?.values[0][currentLocale],
        description:
            metadataAttributes?.meta_description?.values[0][currentLocale],
    };
    const [errors, setErrors] = useState({
        product: '',
        date: '',
        question: '',
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        address: '',
        zipCode: '',
        city: '',
        countryName: '',
        provinceName: '',
    });
    const { t } = useTranslation('pages');
    const { addAlert } = useContext(feedbackContext);

    const isFormValid = useCallback(() => {
        let isValid = true;

        for (const value of Object.values(errors)) {
            if (value !== '') {
                isValid = false;
                break;
            }
        }

        if (product.trim() === '') {
            isValid = false;
            setErrors((errors) => ({
                ...errors,
                product: t('error.field-required'),
            }));
        }
        if (question.trim() === '') {
            isValid = false;
            setErrors((errors) => ({
                ...errors,
                question: t('error.field-required'),
            }));
        }
        if (date.trim() === 'YYYY-MM-DD') {
            isValid = false;
            setErrors((errors) => ({
                ...errors,
                date: t('error.field-required'),
            }));
        }
        if (firstName.trim() === '') {
            isValid = false;
            setErrors((errors) => ({
                ...errors,
                firstName: t('error.field-required'),
            }));
        }
        if (lastName.trim() === '') {
            isValid = false;
            setErrors((errors) => ({
                ...errors,
                lastName: t('error.field-required'),
            }));
        }
        if (email.trim() === '') {
            isValid = false;
            setErrors((errors) => ({
                ...errors,
                email: t('error.field-required'),
            }));
        }
        if (phoneNumber.trim() === '') {
            isValid = false;
            setErrors((errors) => ({
                ...errors,
                phoneNumber: t('error.field-required'),
            }));
        }
        if (address.trim() === '') {
            isValid = false;
            setErrors((errors) => ({
                ...errors,
                address: t('error.field-required'),
            }));
        }
        if (zipCode.trim() === '') {
            isValid = false;
            setErrors((errors) => ({
                ...errors,
                zipCode: t('error.field-required'),
            }));
        }
        if (city.trim() === '') {
            isValid = false;
            setErrors((errors) => ({
                ...errors,
                city: t('error.field-required'),
            }));
        }
        if (countryName.trim() === '') {
            isValid = false;
            setErrors((errors) => ({
                ...errors,
                countryName: t('error.field-required'),
            }));
        }

        if (isProvinceNameRequired && provinceName.trim() === '') {
            isValid = false;
            setErrors((errors) => ({
                ...errors,
                provinceName: t('error.field-required'),
            }));
        }

        if (!isValid) {
            addAlert({
                message: t('toastMessages:invalid-form'),
                status: 'error',
            });
        }

        return isValid;
    }, [
        product,
        question,
        date,
        firstName,
        lastName,
        email,
        phoneNumber,
        address,
        zipCode,
        city,
        countryName,
        provinceName,
        isProvinceNameRequired,
        errors,
        t,
        addAlert,
    ]);

    const convertDate = () => {
        const dateArray = date.split('-');
        return dateArray[2] + '/' + dateArray[1] + '/' + dateArray[0];
    };

    const submitForm = useCallback(
        async (event) => {
            setIsLoading(true);
            event.preventDefault();
            const requestOptions = {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    language: currentLocale == 'fr_CA' ? 'Français' : 'English',
                    origine: 'Drolet',
                    sku: product.split(' ')[0],
                    plannedPurchase: convertDate(),
                    description: question,
                    firstname: firstName,
                    lastname: lastName,
                    telephone: phoneNumber,
                    email: email,
                    zipCode: zipCode,
                    street: address,
                    city: city,
                    state: provinceName,
                    country: countryName,
                }),
            };

            if (isFormValid()) {
                const response = await fetch(
                    '/inquiry/wtc-customer-support',
                    requestOptions
                );

                if (response.ok) {
                    navigateTranslated(`/contact/`);
                    addAlert({
                        message: t('toastMessages:contact-form-success'),
                        status: 'success',
                    });
                } else {
                    addAlert({
                        message: t('toastMessages:contact-form-failed'),
                        status: 'error',
                    });
                    setIsLoading(false);
                }
            } else {
                setIsLoading(false);
            }
        },
        [
            addAlert,
            address,
            city,
            countryName,
            provinceName,
            date,
            email,
            firstName,
            isFormValid,
            lastName,
            navigateTranslated,
            phoneNumber,
            product,
            question,
            t,
            zipCode,
        ]
    );

    return (
        <Layout>
            <SEO title={metadata?.title} description={metadata?.description} />
            <div className='container'>
                <div className='contactForm'>
                    <h1>{t('contact-forms.customer-form-title')}</h1>
                    <p>{t('contact-forms.contact-form-header1')}</p>
                    <p>{t('contact-forms.contact-form-header2')}</p>
                    <form
                        id='customer-service-form'
                        onSubmit={(event) => submitForm(event)}
                    >
                        <h3>
                            {t('contact-forms.choose-a-product-2')}
                            <span className='asterisk'>{' *'}</span>
                        </h3>
                        <div className='row'>
                            <div className='group'>
                                <Select
                                    error={errors.product}
                                    className='dropdown'
                                    firstOption={t(
                                        'contact-forms.choose-a-product'
                                    )}
                                    elements={data.allProductsJson.nodes}
                                    value={product}
                                    displayAttribute='title'
                                    lang={currentLocale}
                                    key='code'
                                    selectType='product'
                                    onChange={(value) => setProduct(value)}
                                    validateInput={() => {
                                        if (product === '')
                                            setErrors({
                                                ...errors,
                                                product: t(
                                                    'error.field-required'
                                                ),
                                            });
                                        else
                                            setErrors({
                                                ...errors,
                                                product: '',
                                            });
                                    }}
                                />
                            </div>
                        </div>
                        <br />
                        <div className='row'>
                            <div className='group'>
                                <h3>
                                    {t('contact-forms.expected-purchase-date')}
                                </h3>
                                <Input
                                    className='datePicker dropdown'
                                    error={errors.date}
                                    type='date'
                                    value={date}
                                    onChange={(value) => {
                                        setDate(value);
                                    }}
                                    validateInput={() => {
                                        if (date === 'YYYY-MM-DD')
                                            setErrors({
                                                ...errors,
                                                date: t('error.field-required'),
                                            });
                                        else
                                            setErrors({
                                                ...errors,
                                                date: '',
                                            });
                                    }}
                                />
                            </div>
                        </div>

                        <br />
                        <br />
                        <h3>
                            {t('contact-forms.question')}
                            <span className='asterisk'>{' *'}</span>
                        </h3>
                        <div className='row'>
                            <strong>
                                {t('contact-forms.question-details')}
                            </strong>
                        </div>
                        <div className='row'>
                            <div className='questionBox'>
                                <textarea
                                    className={
                                        errors.question
                                            ? 'commentsBox error'
                                            : 'commentsBox'
                                    }
                                    onChange={(event) => {
                                        setQuestion(event.target.value);

                                        if (question === '')
                                            setErrors({
                                                ...errors,
                                                question: t(
                                                    'error.field-required'
                                                ),
                                            });
                                        else
                                            setErrors({
                                                ...errors,
                                                question: '',
                                            });
                                    }}
                                />
                                <span
                                    className='inputError'
                                    style={{
                                        display: errors.question
                                            ? 'inline'
                                            : 'none',
                                    }}
                                >
                                    {errors.question}
                                </span>
                            </div>
                        </div>
                        <br />
                        <br />
                        <h3>
                            {t('contact-forms.contact-info-prompt')}
                            <span className='asterisk'>{' *'}</span>
                        </h3>
                        <div className='clientInfos'>
                            <div className='row'>
                                <div className='group'>
                                    <label>
                                        {t('contact-forms.firstname')}
                                        <span className='asterisk'>{' *'}</span>
                                    </label>
                                    <Input
                                        type='text'
                                        error={errors.firstName}
                                        value={firstName}
                                        onChange={(value) =>
                                            setFirstName(value)
                                        }
                                        validateInput={() => {
                                            if (firstName === '')
                                                setErrors({
                                                    ...errors,
                                                    firstName: t(
                                                        'error.field-required'
                                                    ),
                                                });
                                            else
                                                setErrors({
                                                    ...errors,
                                                    firstName: '',
                                                });
                                        }}
                                    />
                                </div>

                                <div className='group'>
                                    <label>
                                        {t('contact-forms.lastname')}
                                        <span className='asterisk'>{' *'}</span>
                                    </label>
                                    <Input
                                        type='text'
                                        error={errors.lastName}
                                        value={lastName}
                                        onChange={(value) => setLastName(value)}
                                        validateInput={() => {
                                            if (lastName === '')
                                                setErrors({
                                                    ...errors,
                                                    lastName: t(
                                                        'error.field-required'
                                                    ),
                                                });
                                            else
                                                setErrors({
                                                    ...errors,
                                                    lastName: '',
                                                });
                                        }}
                                    />
                                </div>
                            </div>
                            <div className='row'>
                                <div className='group'>
                                    <label>
                                        {t('contact-forms.contact-email')}
                                        <span className='asterisk'>{' *'}</span>
                                    </label>
                                    <Input
                                        type='text'
                                        error={errors.email}
                                        value={email}
                                        onChange={(value) => setEmail(value)}
                                        validateInput={() => {
                                            if (email === '')
                                                setErrors({
                                                    ...errors,
                                                    email: t(
                                                        'error.field-required'
                                                    ),
                                                });
                                            else if (!validateEmail(email))
                                                setErrors({
                                                    ...errors,
                                                    email: t(
                                                        'error.invalid-email'
                                                    ),
                                                });
                                            else
                                                setErrors({
                                                    ...errors,
                                                    email: '',
                                                });
                                        }}
                                    />
                                </div>

                                <div className='group'>
                                    <label>
                                        {t('contact-forms.phone')}
                                        <span className='asterisk'>{' *'}</span>
                                    </label>
                                    <Input
                                        type='tel'
                                        value={phoneNumber}
                                        error={errors.phoneNumber}
                                        onChange={(value) =>
                                            setPhoneNumber(
                                                value.replace(/\D/g, '')
                                            )
                                        }
                                        validateInput={() => {
                                            if (phoneNumber === '')
                                                setErrors({
                                                    ...errors,
                                                    phoneNumber: t(
                                                        'error.field-required'
                                                    ),
                                                });
                                            else
                                                setErrors({
                                                    ...errors,
                                                    phoneNumber: '',
                                                });
                                        }}
                                    />
                                </div>
                            </div>
                            <div className='row address'>
                                <div className='group'>
                                    <label>
                                        {t('contact-forms.address')}
                                        <span className='asterisk'>{' *'}</span>
                                    </label>
                                    <Input
                                        type='text'
                                        error={errors.address}
                                        value={address}
                                        onChange={(value) => setAddress(value)}
                                        validateInput={() => {
                                            if (address === '')
                                                setErrors({
                                                    ...errors,
                                                    address: t(
                                                        'error.field-required'
                                                    ),
                                                });
                                            else
                                                setErrors({
                                                    ...errors,
                                                    address: '',
                                                });
                                        }}
                                    />
                                </div>
                            </div>
                            <div className='row'>
                                <div className='group'>
                                    <label>
                                        {t('contact-forms.postcode')}
                                        <span className='asterisk'>{' *'}</span>
                                    </label>
                                    <Input
                                        type='text'
                                        error={errors.zipCode}
                                        value={zipCode}
                                        onChange={(value) => setZipCode(value)}
                                        validateInput={() => {
                                            if (zipCode === '')
                                                setErrors({
                                                    ...errors,
                                                    zipCode: t(
                                                        'error.field-required'
                                                    ),
                                                });
                                            else
                                                setErrors({
                                                    ...errors,
                                                    zipCode: '',
                                                });
                                        }}
                                    />
                                </div>

                                <div className='group'>
                                    <label>
                                        {t('contact-forms.city')}
                                        <span className='asterisk'>{' *'}</span>
                                    </label>
                                    <Input
                                        value={city}
                                        error={errors.city}
                                        type='text'
                                        onChange={(value) => setCity(value)}
                                        validateInput={() => {
                                            if (city === '')
                                                setErrors({
                                                    ...errors,
                                                    city: t(
                                                        'error.field-required'
                                                    ),
                                                });
                                            else
                                                setErrors({
                                                    ...errors,
                                                    city: '',
                                                });
                                        }}
                                    />
                                </div>
                            </div>
                            <div className='row'>
                                <div className='group'>
                                    <label>
                                        {t('contact-forms.country')}
                                        <span className='asterisk'>{' *'}</span>
                                    </label>

                                    <CountryNameSelect
                                        countryName={countryName}
                                        onChange={(value: string) =>
                                            setCountryName(value)
                                        }
                                        firstOption={t(
                                            'contact-forms.country-select-placeholder'
                                        )}
                                        errorMessage={errors.countryName}
                                        validateValue={(value: string) => {
                                            setErrors({
                                                ...errors,
                                                countryName:
                                                    value === ''
                                                        ? t(
                                                              'error.field-required'
                                                          )
                                                        : '',
                                            });
                                        }}
                                    />
                                </div>

                                <div className='group'>
                                    <label>
                                        {t('contact-forms.province')}
                                        {isProvinceNameRequired && (
                                            <span className='asterisk'>
                                                {' *'}
                                            </span>
                                        )}
                                    </label>

                                    <ProvinceNameInput
                                        setIsRequired={
                                            setIsProvinceNameRequired
                                        }
                                        setProvinceName={setProvinceName}
                                        countryName={countryName}
                                        provinceName={provinceName}
                                        selectFirstOption={t(
                                            'contact-forms.province-select-placeholder'
                                        )}
                                        onChange={(value) =>
                                            setProvinceName(value)
                                        }
                                        errorMessage={errors.provinceName}
                                        validateValue={() => {
                                            setErrors({
                                                ...errors,
                                                provinceName:
                                                    provinceName === ''
                                                        ? t(
                                                              'error.field-required'
                                                          )
                                                        : '',
                                            });
                                        }}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className='btnWrap'>
                            <DroletButton
                                isLoading={isLoading}
                                label={t('warranty.form.submit')}
                            />
                        </div>
                    </form>
                </div>
            </div>
        </Layout>
    );
}
export default CustomerService;
